import clsx from 'clsx'
import { useMemo, useState } from 'react'
import NextLink from 'next/link'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import { PelaksanaanAppealRollbackDialog } from 'app/KinerjaGpkPage/components/PelaksanaanAppeal/PelaksanaanAppealRollbackDialog'
import {
  PRAKTIK_KINERJA_STEP_ORDER,
  PRAKTIK_KINERJA_STEPS,
} from 'app/KinerjaGpkPage/constants/praktik-kinerja-steps'
import { PraktikKinerjaStepCard } from 'app/KinerjaShared/components/PraktikKinerjaStepCard'
import useAppealRollback from 'app/KinerjaGpkPage/hooks/use-appeal-rollback'
import { API_PATH_PELAKSANAAN_PROGRESS } from 'api/kinerja/useAPIKinerja'
import { API_GET_RENCANA_TINDAK_LANJUT_QUERY_KEY } from 'api/kinerja/gpk/pelaksanaan'
import { PelaksanaanAppealRequestDialog } from 'app/KinerjaGpkPage/components/PelaksanaanAppeal/PelaksanaanAppealRequestDialog'
import { PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'

import type { StepCardStatus } from 'app/KinerjaShared/components/PraktikKinerjaStepCard'
import type {
  TPelaksanaanAppealableStep,
  TFollowUpEvaluationDocumentStates,
  TStatePelaksanaan,
} from 'types/kinerja-pelaksanaan'

type RencanaTindakLanjutStepCardProps = {
  appealState?: TPelaksanaanAppealableStep
  currentState?: TStatePelaksanaan
  latestFormState?: TFollowUpEvaluationDocumentStates
}

const STEP: TStatePelaksanaan = 'DOCUMENT_FOLLOW_UP'

/**
 * @deprecated Use `RencanaTindakLanjutStepCard` from
 * `app/KinerjaShared/components/PraktikKinerjaPegawaiStepCards` for new
 * implementations across roles.
 */
export function RencanaTindakLanjutStepCard({
  appealState,
  currentState,
  latestFormState,
}: RencanaTindakLanjutStepCardProps) {
  const queryClient = useQueryClient()
  const { isAppealRollbackEnabled } = useAppealRollback()

  // ----------------------------------------------------
  // Dialog state
  // ----------------------------------------------------
  const [activeDialog, setActiveDialog] = useState<
    'ROLLBACK' | 'APPEAL' | 'NONE'
  >('NONE')

  // ----------------------------------------------------
  // Resolve step card status
  // ----------------------------------------------------
  const stepCardStatus: StepCardStatus = useMemo(() => {
    const currentStepIndex = PRAKTIK_KINERJA_STEP_ORDER.indexOf(currentState)
    const rencanaTindakLanjutStepIndex =
      PRAKTIK_KINERJA_STEP_ORDER.indexOf(STEP)

    const appealStepIndex = PRAKTIK_KINERJA_STEP_ORDER.indexOf(appealState)
    const isAppealingPriorSteps =
      appealState && appealStepIndex < rencanaTindakLanjutStepIndex

    if (currentStepIndex > rencanaTindakLanjutStepIndex) return 'completed'
    if (
      currentStepIndex === rencanaTindakLanjutStepIndex &&
      !isAppealingPriorSteps
    )
      return 'active'

    return 'locked'
  }, [currentState, appealState])

  // ----------------------------------------------------
  // Step data
  // ----------------------------------------------------
  const stepData = PRAKTIK_KINERJA_STEPS[STEP]

  // ----------------------------------------------------
  // Handlers
  // ----------------------------------------------------
  const handleClickEdit = () => {
    if (
      currentState === 'FOLLOW_UP_EVALUATION' &&
      latestFormState === 'NOT_CREATED'
    ) {
      // Immediately show rollback confirmation dialog
      // If confirmed, the form will immediately be set to DRAFT state
      setActiveDialog('ROLLBACK')
    } else {
      setActiveDialog('APPEAL')
    }
  }

  // ----------------------------------------------------
  // Other derived values
  // ----------------------------------------------------
  const isAppealing = appealState === STEP

  const showAppealCTA =
    isAppealRollbackEnabled && stepCardStatus === 'completed'
  const isAppealRollbackDisabled = !!appealState && appealState !== STEP
  const shouldVisitObservasiPageFirst =
    currentState === STEP && latestFormState === 'NOT_CREATED'
  const observasiPath = `${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja/observasi-rencana-tindak-lanjut`

  return (
    <>
      <PraktikKinerjaStepCard
        className="max-w-96"
        actions={
          <div className="flex gap-2 flex-wrap">
            <NextLink
              href={
                shouldVisitObservasiPageFirst
                  ? observasiPath
                  : stepData.getPath()
              }
              passHref
            >
              <Button
                as="a"
                size="lg"
                color={stepCardStatus === 'active' ? 'blue' : 'white'}
                className="!inline-flex whitespace-nowrap"
                type={undefined}
              >
                {stepCardStatus === 'active'
                  ? latestFormState === 'DRAFT'
                    ? 'Lanjutkan'
                    : 'Lakukan'
                  : 'Cek rincian'}
              </Button>
            </NextLink>

            {showAppealCTA ? (
              isAppealing ? (
                <div className="flex gap-1 items-center py-3 animate-pulse">
                  <Icon
                    color="informational"
                    className="motion-safe:animate-spin"
                  >
                    hourglass_empty
                  </Icon>
                  <Text className="!text-blue-60" color="informational">
                    Perubahan sedang diajukan.
                  </Text>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="lg"
                  color="blue"
                  disabled={isAppealRollbackDisabled}
                  className={clsx(
                    'gap-1 !bg-white',
                    isAppealRollbackDisabled && '!text-blue-40'
                  )}
                  onClick={handleClickEdit}
                >
                  <Icon color="inherit">edit_outline</Icon>
                  Ubah
                </Button>
              )
            ) : null}
          </div>
        }
        iconSrc="/illustrations/img-document-interaction-discussion.png"
        status={stepCardStatus}
        lockedHint="Menunggu aksi dari Tim Kinerja."
        title={
          <>
            Pembinaan Kinerja:
            <br />
            Isi {stepData.title}
          </>
        }
        description="Refleksi dan diskusi dengan Tim Kinerja untuk isi dukungan yang dibutuhkan."
      />

      <PelaksanaanAppealRollbackDialog
        isOpen={activeDialog === 'ROLLBACK'}
        onClose={() => setActiveDialog('NONE')}
        onCancel={() => setActiveDialog('NONE')}
        step={STEP as TPelaksanaanAppealableStep}
        url={stepData.getPath()}
        onSuccess={() => {
          // Invalidate without auto-refetch to prevent double fetch
          // React Query will refetch when needed
          // and deduplicate if multiple components share the same query key
          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS, {
            refetchActive: false,
            refetchInactive: false,
          })
          queryClient.invalidateQueries(
            API_GET_RENCANA_TINDAK_LANJUT_QUERY_KEY,
            {
              refetchActive: false,
              refetchInactive: false,
            }
          )
        }}
      />

      <PelaksanaanAppealRequestDialog
        isOpen={activeDialog === 'APPEAL'}
        currentStep={currentState}
        appealStep={STEP as TPelaksanaanAppealableStep}
        onClose={() => setActiveDialog('NONE')}
      />
    </>
  )
}
